import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import AdminTable from '../../../components/AdminTable';
import DashboardHeaderWithFilters from '../../../components/DashboardHeaderWithFilters';
import EmptyRecord from '../../../components/EmptyRecord';
import useFilterMethods from '../../../customHooks/useFilterMethods';
import useGetQueryParams from '../../../customHooks/useGetQueryParams';
import useTableColumn from '../../../customHooks/useTableColumn';
import useToast from '../../../customHooks/useToast';
import { GET_ALL_REWARDS } from '../../../graphql/admin_queries';
import useCustomQuery from '../../../network/useCustomQuery';
import EmptyMerchantSettlement from '../../../svgs/EmptyMerchantSettlement';
import generateCSV from '../../../utils/generateCSV';
import generatePDF from '../../../utils/generatePdf';
import { cleanUpRewards } from '../../../utils/helpers';
import { AdminRouteName } from '../../CONSTANTS';
import Styles from './RewardRecordSection.module.scss';

const limit = 14;
const RewardRecordSection = () => {
  const Toast = useToast();
  const [getRewards, rewardLoading, rewards] = useCustomQuery({
    query: GET_ALL_REWARDS,
    onError: () => {},
    onData: () => {},
  });
  const { getQuery, getFilterQueries } = useGetQueryParams();
  const search = getQuery('search') || '';
  const page = Number(getQuery('page'));
  const { push } = useHistory();

  const allColumns = [
    ['image', 'email', 'hasBottomValue', 'name'],
    'enrollmentType',
    'location',
    'totalReferred',
    'earnings',
    'status',
    'referralCode',
    'completedReferrals',
    'date',
  ];
  const columnLabelFormat = {
    name: 'Customer',
    enrollmentType: 'Canadian Status',
    location: 'Location',
    totalReferred: 'Total Referred',
    status: 'Status',
    earnings: 'Earnings',
    referralCode: 'Referral Code',
    completedReferrals: 'Completed Referrals',
    date: 'Date Joined',
  };
  const { getColumns } = useTableColumn(allColumns, 'reward');

  const [rewardsRec, setRewardsRec] = useState(null);
  const setPage = (pageNum) => {
    const searchParams = new URLSearchParams(window.location.search);
    searchParams.set('page', pageNum);
    push({
      search: searchParams.toString(),
    });
  };
  if (!page) {
    push(`/rewards?page=1&search=${search}`);
  }

  const onSearchChange = (e) => {
    const result = getFilterQueries();
    push(`${AdminRouteName?.rewardsRoute}?page=1&search=${encodeURIComponent(e.target.value)}&from=${result?.from}&to=${result?.to}&fromDate=${result?.fromDate}&toDate=${result?.to}&fromDateTable=${result?.fromDateTable}&toDateTable=${result?.toDateTable}`);
  };

  useEffect(() => {
    if (page) {
      const result = getFilterQueries();
      getRewards({
        variables: {
          offset: (page - 1) * limit,
          limit,
          search,
          filters: {
            from: result?.fromDateTable,
            to: result?.toDateTable,
          },
        },
      });
    }
  }, [page, search]);

  const getRewardFunc = (result) => {
    getRewards({
      variables: {
        offset: 0,
        limit,
        search,
        filters: {
          from: result?.fromDateTable,
          to: result?.toDateTable,

        },
      },
    });
  };

  const onFilterSubmit = (result) => {
    getRewardFunc(result);
  };

  const {
    filterObj,
  } = useFilterMethods({
    allowedFilters: ['tableDateRange'],
    defaultUrl: '/rewards?page=1',
    cb: onFilterSubmit,
    Toast,
  });

  useEffect(() => {
    if (rewards?.getTopReferrals?.referrals) {
      setRewardsRec(rewards?.getTopReferrals);
    }
  }, [rewards]);

  const [getExportRecord] = useCustomQuery({
    query: GET_ALL_REWARDS,
    onError: () => {},
    onData: () => {},
  });

  const handleExport = async (type: string) => {
    try {
      const result = getFilterQueries();
      const output = await getExportRecord({
        variables: {
          offset: 0,
          limit: 2000,
          search,
          filter: {
            from: result?.fromDateTable,
            to: result?.toDateTable,
          },
        },
      });
      if (output?.data?.getTopReferrals?.referrals) {
        const rec = cleanUpRewards(output?.data?.getTopReferrals?.referrals);
        const func = type === 'pdf' ? generatePDF : generateCSV;
        const selectedHeaders = [
          'S/N',
          // change image__email__hasBottomValue__name concated columns to a single column of name
          ...(getColumns?.()?.map((column) => (column === 'image__email__hasBottomValue__name' ? ['name', 'email'] : column)) || []),
        ];

        func({
          payload: rec,
          title: 'Rewards',
          selectedHeaders,
          formatHeaders: columnLabelFormat,
          cb: () => Toast.success('Exported successfully'),
        });
      }
    } catch (error) {
      Toast.error('Error while exporting');
    }
  };

  const handleRoute = (data:any) => {
    push(`/rewards/rewards-details/${data?.userId
    }`);
  };

  return (
    <div className={Styles.wrapper}>
      <div className={Styles.wrapper__header}>
        <DashboardHeaderWithFilters
          title="Top referrals"
          filterObj={filterObj}
          dateType="tableDateRange"
          showSearch
          search={search}
          onSearchChange={onSearchChange}
          searchPlaceholder="Customer name /  Email address"
          exportObj={{
            handleExport,
          }}
        />
      </div>

      <div className="transaction-table-details-cover">
        {rewardsRec?.count ? (
          <AdminTable
            refreshing={rewardLoading}
            page={page}
            setPage={setPage}
            pageSize={limit}
            minHeight="calc(100vh - 280px)"
            count={rewardsRec?.count}
            contents={cleanUpRewards(rewardsRec?.referrals)}
            reference="id"
            hideDominantImage
            hasNameAndImage
            clickable
            handleClick={handleRoute}
            allowKeyArr={getColumns(true)}
            mobileAllowKeyArr={
              [
                'status',
                ['name', 'email'],
                ['location', 'earnings'],
              ]
            }
            formatLabels={columnLabelFormat}
          />
        ) : (
          <EmptyRecord
            height="calc(100vh - 240px)"
            title={rewardLoading ? 'Fetching rewards...' : 'No Rewards yet'}
            main="All rewards from customers on your Payfi will appear here"
            // footer="Receive Payments"
            loading={rewardLoading}
            Icon={EmptyMerchantSettlement}
          />
        )}

      </div>
    </div>
  );
};

export default RewardRecordSection;
